import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Card,
  Row,
  Col,
  Badge,
  Button,
  Dropdown,
  Alert,
  Form,
} from "react-bootstrap";
import {
  Calendar,
  Clock,
  DollarSign,
  Users,
  Star,
  Plus,
  X,
} from "lucide-react";
import moment from "moment";
import {
  getAllMaids,
  verifyPayment,
} from "../../../../redux/Actions/maidAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

export default function TipVerificationModal({
  show,
  onHide,
  team: initialTeam,
  service,
}) {
  const dispatch = useDispatch();

  const { maids: allMaids } = useSelector((state) => state.maidsList);
  const { userInfo } = useSelector((state) => state.userLogin);
  const { loading: payoutLoading, success: payoutSuccess } = useSelector(
    (state) => state.verifyPayment,
  );

  const valets = allMaids.filter((maid) => maid.type == service);

  const [team, setTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [membersError, setMembersError] = useState(false);
  const [inactiveError, setInactiveError] = useState(false);

  useEffect(() => {
    if (!show || !initialTeam) return;

    dispatch(getAllMaids(initialTeam.hotel, service));
  }, [show, valets.length]);

  useEffect(() => {
    if (!show || !initialTeam) return;

    if (show && initialTeam) {
      setTeam({ ...initialTeam });
      setSearchQuery("");
      setMembersError(false);
      setInactiveError(false);
    }
  }, [show, initialTeam]);

  const getTime = (ts) => (ts ? moment(ts).format("h:mm A") : "N/A");

  const handleAdd = (valet) => {
    if (team.teamMembers.some((m) => m._id === valet._id)) return;
    setTeam((t) => ({
      ...t,
      teamMembers: [...t.teamMembers, valet],
    }));
  };

  const handleRemove = (memberId) => {
    setTeam((t) => ({
      ...t,
      teamMembers: t.teamMembers.filter((m) => m._id !== memberId),
    }));
  };

  const handleVerify = () => {
    setMembersError(false);
    setInactiveError(false);

    const members = team.teamMembers || [];
    if (members.length === 0) {
      setMembersError(true);
      return;
    }
    const hasInactive =
      !userInfo.model.b2b && members.some((m) => !m.chargesStatus);
    if (hasInactive) {
      setInactiveError(true);
      return;
    }

    const tipAmountPerMember = team.tip / members.length;

    dispatch(
      verifyPayment(
        userInfo.isAdmin ? userInfo._id : userInfo.hotel,
        team._id,
        members,
        tipAmountPerMember,
        team.review,
        team.paymentIntentId,
        team.customer,
      ),
    );
  };

  const handleClose = () => {
    setTeam(null);
    onHide();
  };

  if (!team) return null;

  const tipPerMember = team.teamMembers?.length
    ? (team.tip / team.teamMembers.length).toFixed(2)
    : "0.00";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
    >
      <Modal.Header className="bg-primary text-white border-0">
        <Modal.Title className="d-flex align-items-center">
          Tip Verification
        </Modal.Title>
        <Button
          variant="link"
          className="text-white p-0 border-0"
          onClick={handleClose}
        >
          <X size={20} />
        </Button>
      </Modal.Header>

      <Modal.Body className="p-4">
        {membersError && (
          <Alert variant="danger" className="mb-3">
            Please select at least one team member.
          </Alert>
        )}
        {inactiveError && (
          <Alert variant="danger" className="mb-3">
            All selected members must have an active charge status.
          </Alert>
        )}
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2 text-muted mb-2">
                  <Calendar size={16} />
                  <small className="fw-medium">Period</small>
                </div>
                <div className="fw-semibold">
                  {(() => {
                    const hasStart =
                      team.startDate && moment(team.startDate).isValid();
                    const hasEnd =
                      team.endDate && moment(team.endDate).isValid();

                    if (hasStart && hasEnd) {
                      return `${moment(team.startDate).format(
                        "MM/DD/YYYY",
                      )} → ${moment(team.endDate).format("MM/DD/YYYY")}`;
                    }
                    if (hasStart) {
                      return `From ${moment(team.startDate).format(
                        "MM/DD/YYYY",
                      )}`;
                    }
                    if (hasEnd) {
                      return `Until ${moment(team.endDate).format(
                        "MM/DD/YYYY",
                      )}`;
                    }

                    return "No Date Provided";
                  })()}
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex align-items-center gap-2 text-muted mb-2">
                  <Clock size={16} />
                  <small className="fw-medium">Created At</small>
                </div>
                <div className="fw-semibold">
                  {team.createdAt
                    ? moment(team.createdAt).format("MM/DD/YYYY • h:mm A")
                    : "N/A"}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="mb-3">
          <Col md={6}>
            <Card className="h-100 border-success border-2">
              <Card.Body className="text-center p-1">
                <DollarSign className="text-success mb-2" size={32} />
                <h4 className="text-success mb-1">${team.tip}</h4>
                <small className="text-muted">Total Tip Amount</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="h-100 border-success border-2">
              <Card.Body className="text-center p-1">
                <Users className="text-success mb-2" size={32} />
                <h4 className="text-success mb-1">${tipPerMember}</h4>
                <small className="text-muted">Per Team Member</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="shadow-sm mb-3">
          <Card.Body className="border-1 d-flex align-items-center gap-2">
            <Star className="text-warning" size={18} />
            <strong>Customer Review</strong>{" "}
            <span className="ms-3 fst-italic">“{team.review}”</span>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-light border-0">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Users size={18} /> Team Members (
                {team.teamMembers?.length || 0})
              </h6>
              {!team.verified && (
                <Dropdown show={showAdd} onToggle={setShowAdd}>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    size="sm"
                    className="d-flex align-items-center gap-1"
                    disabled={team.verified}
                  >
                    <Plus size={16} /> Add Member
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Form.Control
                      type="text"
                      placeholder="Search..."
                      className="mb-2"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {valets
                      .filter((v) =>
                        v.serviceName
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((v) => (
                        <Dropdown.Item key={v._id} onClick={() => handleAdd(v)}>
                          {v.serviceName}
                        </Dropdown.Item>
                      ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {team.teamMembers?.length === 0 ? (
              <div className="text-center text-muted ">
                <Users size={48} className="opacity-50" />
                <p>No team members added yet</p>
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {team.teamMembers?.map((m) => (
                  <Badge
                    key={m._id}
                    bg="primary"
                    className="d-flex align-items-center gap-2 p-2 fs-6"
                  >
                    {/* Initial-avatar circle */}
                    <div
                      className="bg-white text-primary rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "24px",
                        height: "24px",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {m.serviceName.charAt(0)}
                    </div>

                    {/* Charges-status dot */}
                    {!userInfo?.model.b2b && (
                      <span
                        className={`rounded-circle ${
                          m.chargesStatus ? "bg-success" : "bg-danger"
                        }`}
                        style={{ width: "8px", height: "8px" }}
                      />
                    )}

                    {/* Member name */}
                    <span>{m.serviceName}</span>

                    {/* Remove button */}
                    <Button
                      variant="link"
                      size="sm"
                      className="text-white p-0 border-0 ms-1"
                      onClick={() => handleRemove(m._id)}
                      disabled={team.verified}
                    >
                      <X size={14} />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
        {team.verifiedAt && (
          <div className="mt-3 text-center ">
            <small>
              Verified at: {moment(team.verifiedAt).format("MM/DD/YYYY")} -{" "}
              {getTime(team.verifiedAt)}
            </small>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          className="px-4"
        >
          Close
        </Button>
        <Button
          variant="success"
          className="px-4"
          disabled={
            team.teamMembers?.length === 0 || payoutLoading || team.verified
          }
          onClick={handleVerify}
        >
          {payoutLoading ? (
            "Verifying…"
          ) : team.verified ? (
            <>
              <FontAwesomeIcon icon={faCheck} className="me-1" /> Verified
            </>
          ) : (
            "Verify & Distribute"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
